import {
  type CardLink,
  DEAL_STATUSES,
  type DealStatus,
  LINK_PRIORITIES,
  type LinkPriority,
  type MeResponse,
  hasPermission,
} from "@pokifolio/contracts";
import { type SubmitEvent, useId, useState } from "react";
import { toast } from "sonner";

import { useApi } from "../../app/api-context";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Field } from "../../components/ui/field";
import { Input, Select } from "../../components/ui/input";
import { describeError, serverFieldError } from "../../lib/errors";
import { formatMoney } from "../search/format";
import { DEAL_STATUS_LABELS, DEAL_STATUS_TONES, PRIORITY_LABELS, PRIORITY_TONES } from "./card-format";
import { useCardChange } from "./card-queries";

/**
 * The admin's working status and priority for one listing (D-67), and the price paid (admin or who added it).
 * Choosing "Purchased" without a price paid asks for it first, then adds the card to the Portfolio.
 */
export function DealControls({ link, me }: { link: CardLink; me: MeResponse }) {
  const api = useApi();
  const isAdmin = hasPermission(me.permissions, "research.approve");
  const isOwner = link.addedBy.id === me.user.id;
  const [paying, setPaying] = useState<{ thenStatus: DealStatus | null } | null>(null);
  const deal = useCardChange(link.cardId, (body: Parameters<typeof api.updateLinkDeal>[2]) =>
    api.updateLinkDeal(link.cardId, link.id, body),
  );
  const save = (body: Parameters<typeof api.updateLinkDeal>[2], done?: string) =>
    deal.mutate(body, {
      onSuccess: () => {
        if (done !== undefined) toast.success(done);
      },
      onError: (e) => toast.error(describeError(e).message),
    });

  const setStatus = (value: DealStatus) => {
    if (value === "purchased" && link.pricePaid === null) setPaying({ thenStatus: "purchased" });
    else
      save({ dealStatus: value }, value === "purchased" ? "Purchased. Added to the Portfolio." : undefined);
  };

  return (
    <div className="flex flex-col items-start gap-1.5">
      {isAdmin ? (
        <>
          <Select
            aria-label="Status"
            value={link.dealStatus}
            disabled={deal.isPending}
            onChange={(e) => setStatus(e.target.value as DealStatus)}
            className="h-8 w-40"
          >
            {DEAL_STATUSES.map((s) => (
              <option key={s} value={s}>
                {DEAL_STATUS_LABELS[s]}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Priority"
            value={link.priority ?? ""}
            disabled={deal.isPending}
            onChange={(e) =>
              save({ priority: e.target.value === "" ? null : (e.target.value as LinkPriority) })
            }
            className="h-8 w-40"
          >
            <option value="">No priority</option>
            {LINK_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABELS[p]} priority
              </option>
            ))}
          </Select>
        </>
      ) : (
        <Badge tone={DEAL_STATUS_TONES[link.dealStatus]}>{DEAL_STATUS_LABELS[link.dealStatus]}</Badge>
      )}
      {(isAdmin || isOwner) && (
        <button
          type="button"
          className="text-xs text-muted-foreground underline-offset-2 hover:underline"
          onClick={() => setPaying({ thenStatus: null })}
        >
          {link.pricePaid === null
            ? "Add price paid"
            : `Paid ${formatMoney({ amount: link.pricePaid, currency: link.currency })}`}
        </button>
      )}
      {paying !== null && (
        <PricePaidDialog
          link={link}
          busy={deal.isPending}
          purchasing={paying.thenStatus === "purchased"}
          error={deal.error}
          onCancel={() => setPaying(null)}
          onSave={(pricePaid) =>
            deal.mutate(
              { pricePaid, ...(paying.thenStatus === null ? {} : { dealStatus: paying.thenStatus }) },
              {
                onSuccess: () => {
                  setPaying(null);
                  toast.success(
                    paying.thenStatus === "purchased"
                      ? "Purchased. Added to the Portfolio."
                      : "Price paid saved.",
                  );
                },
              },
            )
          }
        />
      )}
    </div>
  );
}

/** A small "priority" badge shown next to the price. */
export function PriorityBadge({ priority }: { priority: LinkPriority | null }) {
  if (priority === null) return null;
  return <Badge tone={PRIORITY_TONES[priority]}>{PRIORITY_LABELS[priority]}</Badge>;
}

function PricePaidDialog({
  link,
  busy,
  purchasing,
  error,
  onCancel,
  onSave,
}: {
  link: CardLink;
  busy: boolean;
  purchasing: boolean;
  error: unknown;
  onCancel: () => void;
  onSave: (pricePaid: string) => void;
}) {
  const [value, setValue] = useState(link.pricePaid ?? link.price ?? "");
  const id = useId();
  const submit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSave(value);
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{purchasing ? "Purchased: price paid" : "Price paid"}</DialogTitle>
            <DialogDescription>
              {purchasing
                ? "Enter what was paid. The card is then added to the Portfolio."
                : "What was actually paid for this card."}
            </DialogDescription>
          </DialogHeader>
          <Field
            id={id}
            label={`Price paid (${link.currency})`}
            error={error === null ? undefined : serverFieldError(error, "pricePaid")}
          >
            <Input value={value} onChange={(e) => setValue(e.target.value)} inputMode="decimal" />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || value.trim() === ""}>
              {purchasing ? "Save and mark purchased" : "Save"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
